import { ResolverState } from '../../resolvers/definition/resolverState';

export const ON_ATTACH = 'ON_ATTACH' as const;

export const ON_DATA = 'ON_DATA' as const;

export type LinkTiming = typeof ON_ATTACH | typeof ON_DATA;

export type LinkBehavior = {
  state: boolean;
  invalidation: boolean;
};

export type TagWithContext<TContext = unknown> = {
  tag: string;
  context: TContext;
};

export type TagResult = string | TagWithContext;

/**
 * One matched tag between the two linked instances, carrying the context each
 * side contributed for that tag (`undefined` if a side produced a bare-string
 * tag). Transforms get the full set as `contexts` — needed for many-to-many
 * links that must act on every match. For single-match links, `self.context` /
 * `other.context` (the first match) is the simpler read.
 */
export type LinkContextPair = { self: unknown; other: unknown };

export type Link<
  TSelfParams = any,
  TSelfData = any,
  TOtherParams = any,
  TOtherData = any,
> = {
  key: string;

  selfTypeName: string;
  otherTypeName: string;

  timing: LinkTiming;

  tagParams?: {
    self?: (opts: { params: TSelfParams }) => TagResult[] | void;
    other?: (opts: { params: TOtherParams }) => TagResult[] | void;
  };

  tagData?: {
    self?: (opts: {
      data: TSelfData;
      params: TSelfParams;
    }) => TagResult[] | void;
    other?: (opts: {
      data: TOtherData;
      params: TOtherParams;
    }) => TagResult[] | void;
  };

  pushBehavior: LinkBehavior;

  onPush?: (opts: {
    dataChanged: boolean;
    flagsChanged: boolean;
    self: {
      state: ResolverState<TSelfData>;
      params: TSelfParams;
      context?: unknown;
    };
    other: {
      state?: ResolverState<TOtherData>;
      params: TOtherParams;
      context?: unknown;
    };
    getContexts?: () => LinkContextPair[];
  }) => ResolverState<TOtherData> | void;

  pullBehavior: LinkBehavior;

  onPull?: (opts: {
    dataChanged: boolean;
    flagsChanged: boolean;
    self: {
      state?: ResolverState<TSelfData>;
      params: TSelfParams;
      context?: unknown;
    };
    other: {
      state: ResolverState<TOtherData>;
      params: TOtherParams;
      context?: unknown;
    };
    getContexts?: () => LinkContextPair[];
  }) => ResolverState<TSelfData> | void;
};
